// Quaternion utilities for camera rotation

export interface Quaternion {
  x: number
  y: number
  z: number
  w: number
}

// Create quaternion from axis and angle
export function quaternionFromAxisAngle(axis: [number, number, number], angle: number): Quaternion {
  const halfAngle = angle * 0.5
  const s = Math.sin(halfAngle)
  
  // Normalize axis
  const length = Math.sqrt(axis[0] * axis[0] + axis[1] * axis[1] + axis[2] * axis[2])
  const nx = axis[0] / length
  const ny = axis[1] / length
  const nz = axis[2] / length
  
  return {
    x: nx * s,
    y: ny * s,
    z: nz * s,
    w: Math.cos(halfAngle)
  }
}

// Create quaternion from Euler angles (YXZ order for consistency)
export function quaternionFromEuler(rx: number, ry: number, rz: number): Quaternion {
  // YXZ order to match our current system
  const c1 = Math.cos(rx / 2)
  const c2 = Math.cos(ry / 2)
  const c3 = Math.cos(rz / 2)
  const s1 = Math.sin(rx / 2)
  const s2 = Math.sin(ry / 2)
  const s3 = Math.sin(rz / 2)
  
  return {
    x: s1 * c2 * c3 + c1 * s2 * s3,
    y: c1 * s2 * c3 - s1 * c2 * s3,
    z: c1 * c2 * s3 - s1 * s2 * c3,
    w: c1 * c2 * c3 + s1 * s2 * s3
  }
}

// Convert quaternion to Euler angles (YXZ order)
export function quaternionToEuler(q: Quaternion): [number, number, number] {
  const { x, y, z, w } = q
  
  // YXZ order
  const sinr_cosp = 2 * (w * x + y * z)
  const cosr_cosp = 1 - 2 * (x * x + y * y)
  const rx = Math.atan2(sinr_cosp, cosr_cosp)
  
  const sinp = 2 * (w * y - z * x)
  const ry = Math.abs(sinp) >= 1 ? Math.sign(sinp) * Math.PI / 2 : Math.asin(sinp)
  
  const siny_cosp = 2 * (w * z + x * y)
  const cosy_cosp = 1 - 2 * (y * y + z * z)
  const rz = Math.atan2(siny_cosp, cosy_cosp)
  
  return [rx, ry, rz]
}

// Multiply two quaternions
export function quaternionMultiply(a: Quaternion, b: Quaternion): Quaternion {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
  }
}

// Normalize quaternion
export function quaternionNormalize(q: Quaternion): Quaternion {
  const length = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w)
  if (length === 0) {
    return { x: 0, y: 0, z: 0, w: 1 }
  }
  return {
    x: q.x / length,
    y: q.y / length,
    z: q.z / length,
    w: q.w / length
  }
}

// Conjugate (inverse for unit quaternions)
export function quaternionConjugate(q: Quaternion): Quaternion {
  return {
    x: -q.x,
    y: -q.y,
    z: -q.z,
    w: q.w
  }
}

// SLERP (Spherical Linear Interpolation) between two quaternions
export function quaternionSlerp(a: Quaternion, b: Quaternion, t: number): Quaternion {
  // Calculate angle between them
  let dot = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w
  
  // If negative dot, negate one quaternion to take shorter path
  if (dot < 0) {
    b = { x: -b.x, y: -b.y, z: -b.z, w: -b.w }
    dot = -dot
  }
  
  // If very close, do linear interpolation
  if (dot > 0.95) {
    return quaternionNormalize({
      x: a.x + t * (b.x - a.x),
      y: a.y + t * (b.y - a.y),
      z: a.z + t * (b.z - a.z),
      w: a.w + t * (b.w - a.w)
    })
  }
  
  // SLERP
  const angle = Math.acos(dot)
  const sinAngle = Math.sin(angle)
  const wa = Math.sin((1 - t) * angle) / sinAngle
  const wb = Math.sin(t * angle) / sinAngle
  
  return {
    x: wa * a.x + wb * b.x,
    y: wa * a.y + wb * b.y,
    z: wa * a.z + wb * b.z,
    w: wa * a.w + wb * b.w
  }
}

// Convert quaternion to rotation matrix (column-major for WebGL)
export function quaternionToMatrix(q: Quaternion): Float32Array {
  const { x, y, z, w } = q
  
  const x2 = x + x
  const y2 = y + y
  const z2 = z + z
  
  const xx = x * x2
  const xy = x * y2
  const xz = x * z2
  const yy = y * y2
  const yz = y * z2
  const zz = z * z2
  const wx = w * x2
  const wy = w * y2
  const wz = w * z2
  
  return new Float32Array([
    1 - (yy + zz), xy - wz, xz + wy, 0,
    xy + wz, 1 - (xx + zz), yz - wx, 0,
    xz - wy, yz + wx, 1 - (xx + yy), 0,
    0, 0, 0, 1
  ])
}

// Extract quaternion from rotation matrix
export function quaternionFromMatrix(m: Float32Array | number[]): Quaternion {
  const trace = m[0] + m[5] + m[10]
  
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0)
    return {
      w: 0.25 / s,
      x: (m[9] - m[6]) * s,
      y: (m[2] - m[8]) * s,
      z: (m[4] - m[1]) * s
    }
  } else if (m[0] > m[5] && m[0] > m[10]) {
    const s = 2.0 * Math.sqrt(1.0 + m[0] - m[5] - m[10])
    return {
      w: (m[9] - m[6]) / s,
      x: 0.25 * s,
      y: (m[1] + m[4]) / s,
      z: (m[2] + m[8]) / s
    }
  } else if (m[5] > m[10]) {
    const s = 2.0 * Math.sqrt(1.0 + m[5] - m[0] - m[10])
    return {
      w: (m[2] - m[8]) / s,
      x: (m[1] + m[4]) / s,
      y: 0.25 * s,
      z: (m[6] + m[9]) / s
    }
  } else {
    const s = 2.0 * Math.sqrt(1.0 + m[10] - m[0] - m[5])
    return {
      w: (m[4] - m[1]) / s,
      x: (m[2] + m[8]) / s,
      y: (m[6] + m[9]) / s,
      z: 0.25 * s
    }
  }
}

// Identity quaternion
export const IDENTITY_QUATERNION: Quaternion = { x: 0, y: 0, z: 0, w: 1 }